import React, { useEffect, useState } from 'react';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { Star, TrendingUp, ThumbsUp, MessageSquare, Users, ChevronDown, ChevronUp } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ── Star display (read-only) ──────────────────────────────────────────────────
const StarRow = ({ value, color = 'text-amber-400' }) => (
  <div className="flex items-center gap-0.5">
    {[1, 2, 3, 4, 5].map(n => (
      <Star key={n} className={`w-3.5 h-3.5 ${n <= value ? `${color} fill-current` : 'text-slate-200 fill-slate-100'}`} strokeWidth={1.5} />
    ))}
    <span className="ml-1 text-xs font-bold text-slate-600">{value}/5</span>
  </div>
);

// ── Avg score ring ────────────────────────────────────────────────────────────
const ScoreRing = ({ value, label, color }) => {
  const pct = (value / 5) * 100;
  const r = 28, circ = 2 * Math.PI * r;
  const dash = (pct / 100) * circ;
  return (
    <div className="flex flex-col items-center gap-1.5">
      <div className="relative w-20 h-20">
        <svg className="w-20 h-20 -rotate-90" viewBox="0 0 72 72">
          <circle cx="36" cy="36" r={r} fill="none" stroke="#f1f5f9" strokeWidth="8" />
          <circle cx="36" cy="36" r={r} fill="none" stroke={color} strokeWidth="8"
            strokeDasharray={`${dash} ${circ}`} strokeLinecap="round"
            style={{ transition: 'stroke-dasharray 1s ease' }} />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="text-xl font-black text-slate-800" style={{ fontFamily: 'Manrope, sans-serif' }}>
            {value > 0 ? value.toFixed(1) : '—'}
          </span>
        </div>
      </div>
      <p className="text-xs font-semibold text-slate-500 text-center leading-tight">{label}</p>
    </div>
  );
};

// ── Rating card ───────────────────────────────────────────────────────────────
const RatingCard = ({ rating }) => {
  const [expanded, setExpanded] = useState(false);
  const orderLabel = rating.total_clothes != null
    ? `${rating.total_clothes} items`
    : rating.service_summary || 'Laundry order';
  const avgRating = ((rating.delivery_rating + rating.satisfaction_rating) / 2).toFixed(1);
  const isHigh = avgRating >= 4;
  const isMid = avgRating >= 3 && avgRating < 4;

  return (
    <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-all">
      {/* Header strip */}
      <div className={`h-1 w-full ${isHigh ? 'bg-emerald-400' : isMid ? 'bg-amber-400' : 'bg-red-400'}`} />

      <div className="p-4">
        <div className="flex items-start justify-between gap-3">
          {/* Customer + order info */}
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 rounded-xl bg-blue-600 flex items-center justify-center text-white font-black text-sm flex-shrink-0 shadow-sm shadow-blue-200">
              {rating.customer_name?.charAt(0).toUpperCase() || 'C'}
            </div>
            <div className="min-w-0">
              <p className="font-black text-slate-800 text-sm truncate" style={{ fontFamily: 'Manrope, sans-serif' }}>
                {rating.customer_name}
              </p>
              <p className="text-xs text-slate-400 truncate">{orderLabel}</p>
            </div>
          </div>

          {/* Avg score badge */}
          <div className={`flex-shrink-0 text-sm font-black px-3 py-1 rounded-full ${
            isHigh ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' :
            isMid  ? 'bg-amber-50 text-amber-700 border border-amber-200' :
                     'bg-red-50 text-red-700 border border-red-200'
          }`}>
            ★ {avgRating}
          </div>
        </div>

        {/* Star rows */}
        <div className="mt-3 space-y-1.5">
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider">Delivery</span>
            <StarRow value={rating.delivery_rating} />
          </div>
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider">Satisfaction</span>
            <StarRow value={rating.satisfaction_rating} />
          </div>
        </div>

        {/* Comment */}
        {rating.comment && (
          <div className="mt-3 bg-slate-50 rounded-xl px-3 py-2 border border-slate-100">
            <p className="text-xs text-slate-600 italic">"{rating.comment}"</p>
          </div>
        )}

        {/* Toggle order details */}
        <button
          onClick={() => setExpanded(e => !e)}
          className="mt-3 w-full flex items-center justify-between text-xs text-slate-400 hover:text-blue-600 transition-colors"
        >
          <span>{format(new Date(rating.created_at), 'dd MMM yyyy, h:mm a')}</span>
          {expanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
        </button>

        {expanded && (
          <div className="mt-2 pt-2 border-t border-slate-100 space-y-1 text-xs text-slate-500">
            {rating.order_date && (
              <div className="flex justify-between">
                <span>Order date</span>
                <span className="font-medium text-slate-700">{format(new Date(rating.order_date), 'dd MMM yyyy')}</span>
              </div>
            )}
            {rating.pickup_date && (
              <div className="flex justify-between">
                <span>Pickup slot</span>
                <span className="font-medium text-blue-600">
                  {/^\d{4}-\d{2}-\d{2}$/.test(rating.pickup_date)
                    ? rating.pickup_date
                    : format(new Date(rating.pickup_date), 'dd MMM yyyy')}
                  {rating.pickup_time_slot ? ` @ ${rating.pickup_time_slot}` : ''}
                </span>
              </div>
            )}
            {rating.delivery_date && (
              <div className="flex justify-between">
                <span>Delivered</span>
                <span className="font-medium text-emerald-600">{format(new Date(rating.delivery_date), 'dd MMM yyyy, h:mm a')}</span>
              </div>
            )}
            {rating.total_amount > 0 && (
              <div className="flex justify-between">
                <span>Order amount</span>
                <span className="font-medium text-slate-700">₹{rating.total_amount?.toFixed(2)}</span>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// ── Main page ─────────────────────────────────────────────────────────────────
const AdminRatings = () => {
  const { token } = useAuth();
  const [ratings, setRatings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all | 5 | 4 | 3 | low

  useEffect(() => {
    axios.get(`${API}/admin/ratings`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setRatings(r.data))
      .catch(() => toast.error('Failed to load ratings'))
      .finally(() => setLoading(false));
  }, [token]);

  // ── Stats ──
  const total = ratings.length;
  const avgDelivery = total ? ratings.reduce((s, r) => s + r.delivery_rating, 0) / total : 0;
  const avgSatisfaction = total ? ratings.reduce((s, r) => s + r.satisfaction_rating, 0) / total : 0;
  const avgOverall = total ? (avgDelivery + avgSatisfaction) / 2 : 0;
  const fiveStar = ratings.filter(r => r.delivery_rating === 5 && r.satisfaction_rating === 5).length;
  const withComment = ratings.filter(r => r.comment).length;
  const uniqueCustomers = new Set(ratings.map(r => r.customer_id)).size;

  // Distribution for bar chart
  const dist = [5, 4, 3, 2, 1].map(n => ({
    n,
    count: ratings.filter(r => Math.round((r.delivery_rating + r.satisfaction_rating) / 2) === n).length,
  }));
  const maxDist = Math.max(...dist.map(d => d.count), 1);

  // Filter
  const filtered = ratings.filter(r => {
    const avg = (r.delivery_rating + r.satisfaction_rating) / 2;
    if (filter === '5') return avg === 5;
    if (filter === '4') return avg >= 4 && avg < 5;
    if (filter === '3') return avg >= 3 && avg < 4;
    if (filter === 'low') return avg < 3;
    return true;
  });

  if (loading) return (
    <div className="flex items-center justify-center py-20">
      <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
    </div>
  );

  return (
    <div className="space-y-6">

      {/* Header */}
      <div className="bg-gradient-to-br from-blue-600 via-blue-700 to-violet-700 rounded-3xl px-8 py-7 text-white relative overflow-hidden shadow-xl shadow-blue-200">
        <div className="absolute inset-0 opacity-[0.07]" style={{ backgroundImage: 'radial-gradient(circle, #fff 1px, transparent 1px)', backgroundSize: '22px 22px' }} />
        <div className="absolute -right-6 -top-6 w-40 h-40 bg-white/10 rounded-full" />
        <div className="relative z-10">
          <div className="flex items-center gap-2 mb-1">
            <Star className="w-4 h-4 text-amber-300 fill-amber-300" />
            <span className="text-blue-200 text-sm font-semibold">Customer Feedback</span>
          </div>
          <h1 className="text-3xl font-black mb-1" style={{ fontFamily: 'Manrope, sans-serif' }}>Ratings & Reviews</h1>
          <p className="text-blue-200 text-sm">Individual customer post-delivery feedback</p>
        </div>
      </div>

      {total === 0 ? (
        <div className="bg-white border border-slate-200 rounded-3xl py-20 flex flex-col items-center gap-3 text-center shadow-sm">
          <div className="w-16 h-16 bg-slate-50 rounded-full flex items-center justify-center">
            <Star className="w-8 h-8 text-slate-300" strokeWidth={1.5} />
          </div>
          <p className="text-lg font-bold text-slate-500">No ratings yet</p>
          <p className="text-sm text-slate-400">Ratings appear after customers rate their delivered orders</p>
        </div>
      ) : (
        <>
          {/* KPI cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { label: 'Total Ratings', value: total, icon: Star, color: 'bg-amber-50 text-amber-600', border: 'border-l-amber-400' },
              { label: '5-Star Orders', value: fiveStar, icon: ThumbsUp, color: 'bg-emerald-50 text-emerald-600', border: 'border-l-emerald-400' },
              { label: 'With Comments', value: withComment, icon: MessageSquare, color: 'bg-blue-50 text-blue-600', border: 'border-l-blue-400' },
              { label: 'Customers Rated', value: uniqueCustomers, icon: Users, color: 'bg-violet-50 text-violet-600', border: 'border-l-violet-400' },
            ].map(({ label, value, icon: Icon, color, border }) => (
              <div key={label} className={`bg-white border border-slate-200 border-l-4 ${border} rounded-2xl p-4 shadow-sm`}>
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${color}`}>
                    <Icon className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="text-2xl font-black text-slate-800" style={{ fontFamily: 'Manrope, sans-serif' }}>{value}</p>
                    <p className="text-xs text-slate-400 font-medium">{label}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Score rings + distribution */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Average scores */}
            <div className="bg-white border border-slate-200 rounded-3xl p-6 shadow-sm">
              <div className="flex items-center gap-2 mb-5">
                <div className="w-8 h-8 bg-amber-50 rounded-xl flex items-center justify-center">
                  <TrendingUp className="w-4 h-4 text-amber-600" />
                </div>
                <h3 className="font-black text-slate-800 text-sm" style={{ fontFamily: 'Manrope, sans-serif' }}>Average Scores</h3>
              </div>
              <div className="flex items-center justify-around">
                <ScoreRing value={avgDelivery} label="Delivery" color="#3b82f6" />
                <div className="text-center">
                  <div className="w-20 h-20 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center shadow-lg shadow-amber-200">
                    <span className="text-2xl font-black text-white" style={{ fontFamily: 'Manrope, sans-serif' }}>
                      {avgOverall.toFixed(1)}
                    </span>
                  </div>
                  <p className="text-xs font-bold text-slate-500 mt-2">Overall</p>
                </div>
                <ScoreRing value={avgSatisfaction} label="Satisfaction" color="#10b981" />
              </div>
            </div>

            {/* Rating distribution */}
            <div className="bg-white border border-slate-200 rounded-3xl p-6 shadow-sm">
              <div className="flex items-center gap-2 mb-5">
                <div className="w-8 h-8 bg-blue-50 rounded-xl flex items-center justify-center">
                  <Star className="w-4 h-4 text-blue-600" />
                </div>
                <h3 className="font-black text-slate-800 text-sm" style={{ fontFamily: 'Manrope, sans-serif' }}>Rating Distribution</h3>
              </div>
              <div className="space-y-2">
                {dist.map(({ n, count }) => (
                  <div key={n} className="flex items-center gap-2">
                    <div className="flex items-center gap-0.5 w-16 flex-shrink-0">
                      {[...Array(n)].map((_, i) => <Star key={i} className="w-3 h-3 text-amber-400 fill-amber-400" />)}
                    </div>
                    <div className="flex-1 h-5 bg-slate-100 rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all duration-700 ${
                          n >= 4 ? 'bg-emerald-400' : n === 3 ? 'bg-amber-400' : 'bg-red-400'
                        }`}
                        style={{ width: `${(count / maxDist) * 100}%` }}
                      />
                    </div>
                    <span className="text-xs font-bold text-slate-500 w-6 text-right">{count}</span>
                  </div>
                ))}
              </div>
              <p className="text-xs text-slate-400 mt-3">Based on average of delivery + satisfaction</p>
            </div>
          </div>

          {/* Filter tabs */}
          <div className="flex items-center gap-2 flex-wrap">
            {[
              { key: 'all', label: `All (${total})` },
              { key: '5', label: '★★★★★ 5-Star' },
              { key: '4', label: '★★★★ 4-Star' },
              { key: '3', label: '★★★ 3-Star' },
              { key: 'low', label: '< 3 Star' },
            ].map(({ key, label }) => (
              <button
                key={key}
                onClick={() => setFilter(key)}
                className={`text-xs font-bold px-4 py-2 rounded-xl border transition-all ${
                  filter === key
                    ? 'bg-blue-600 text-white border-blue-600 shadow-md shadow-blue-200'
                    : 'bg-white text-slate-600 border-slate-200 hover:border-blue-300 hover:text-blue-600'
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          {/* Rating cards grid */}
          {filtered.length === 0 ? (
            <div className="bg-white border border-slate-200 rounded-2xl py-12 text-center text-slate-400 text-sm">
              No ratings match this filter
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {filtered.map(rating => (
                <RatingCard key={rating.id} rating={rating} />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default AdminRatings;
